<template>
    <v-card>
        <v-card-title>
            <v-row align="center">
                <v-col class="grow"> User Measurements for {{ taskDisplayName }} </v-col>
                <v-col class="shrink text-right">
                    <v-btn color="primary" @click="viewUserMeasurementSet()">
                        Create New User Measurement Set
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-data-table
            :headers="userMeasurementSetsHeaders"
            :items="userMeasurementSets"
            no-results-text="No user measurement sets"
            item-key="index"
            class="elevation-1"
            :items-per-page="25"
        >
            <template v-slot:item.actions="{ item }">
                <v-icon v-if="isEditAvailable(item)" @click="viewUserMeasurementSet(item)"
                    >$pencil</v-icon
                >
                <v-icon v-if="isDeleteAvailable(item)" @click="deleteUserMeasurementSet(item)"
                    >$delete</v-icon
                >
            </template>
        </v-data-table>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="cancel"> Close </v-btn>
        </v-card-actions>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";
import { measurementsAPI } from "@/services/api.services";
import { withLoadingScreen } from "@/lib/loading";
import { findReadoutGroupById, findTaskGroupById, getTaskDisplayName } from "@/lib/taskUtils";
import { mapGetters } from "vuex";

export default defineComponent({
    props: {
        dataCollectionTask: {
            type: Object,
            required: true,
        },
        taskSchedule: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            readoutGroup: null,
            taskGroup: null,
            userMeasurementSets: [],
            userMeasurementSetsHeaders: [
                {
                    title: "User",
                    value: "userId",
                    align: "left",
                    sortable: false,
                    width: "80%",
                },
                {
                    title: "Actions",
                    value: "actions",
                    align: "left",
                    sortable: false,
                    width: "10%",
                },
            ],
            usersWithMeasurementSets: null,
            selectedUserMeasurementSet: null,
            showUserMeasurementsForm: false,
        };
    },

    watch: {
        dataCollectionTask: {
            handler: function () {
                this.loadUserMeasurementSets();
            },
            immediate: true,
        },
    },

    methods: {
        async loadUserMeasurementSets() {
            await withLoadingScreen(async () => {
                this.readoutGroup = findReadoutGroupById(
                    this.dataCollectionTask.readoutGroupId,
                    this.taskSchedule.readoutGroups
                );
                this.taskGroup = findTaskGroupById(
                    this.dataCollectionTask.taskGroupId,
                    this.taskSchedule.taskGroups
                );
                this.userMeasurementSets =
                    await measurementsAPI.fetchUserMeasurementSetsForDataCollectionTaskId(
                        this.dataCollectionTask.id
                    );
                this.usersWithMeasurementSets = this.userMeasurementSets.map(
                    (userMeasurementSet) => userMeasurementSet.userId
                );
            });
        },
        isEditAvailable(userMeasurementSet) {
            return (
                this.isAuthorized("Measurements:Manage") ||
                userMeasurementSet.userId === this.user.email
            );
        },
        isDeleteAvailable(userMeasurementSet) {
            return this.isAuthorized("Measurements:Manage");
        },
        viewUserMeasurementSet(userMeasurementSet) {
            const args = {
                userMeasurementSet,
                dataCollectionTask: this.dataCollectionTask,
                taskGroup: this.taskGroup,
                readoutGroup: this.readoutGroup,
                thingSchema: this.thingSchema,
                usersWithMeasurementSets: this.usersWithMeasurementSets,
            };
            this.$emit("viewUserMeasurementSet", args);
        },
        async deleteUserMeasurementSet(userMeasurementSet) {
            if (
                confirm(
                    `Are you sure you want to delete user measurement set for ${userMeasurementSet.userId}?`
                )
            ) {
                await withLoadingScreen(async () => {
                    await measurementsAPI.deleteUserMeasurementSet(userMeasurementSet.id);
                    await this.loadUserMeasurementSets();
                });
            }
        },
        cancel() {
            this.$emit("close");
        },
    },

    computed: {
        ...mapGetters({
            user: "getAuthUser",
            isAuthorized: "isAuthorized",
        }),
        taskDisplayName() {
            return getTaskDisplayName(
                this.dataCollectionTask,
                this.taskGroup,
                this.readoutGroup,
                this.experiment.day0
            );
        },
    },
});
</script>
<style></style>
